/**
 * @fileoverview Lightweight inline-SVG charts (no chart library):
 * - FlowBarChart: per-room actual vs design flow
 * - TempSparkline: temperature history of one room
 * - HeatingCurvePreview: the configured weather-compensation curve
 *
 * @module HydraulicBalancingTesting/components/Charts
 */

import React from 'react';

import { COLORS, HEAT_PUMP, OUTDOOR } from '../constants';
import { toLitersPerHour, heatingCurveSupply } from '../physics';
import { deviationColor } from './RoomUnit';

// ----------------------------------------------------------------------------
// Flow bar chart
// ----------------------------------------------------------------------------

export const FlowBarChart = ({ rooms, deviations, roomFlows, showTargets = true }) => {
  const W = 400;
  const H = 170;
  const padL = 34;
  const padB = 26;
  const padT = 14;
  const flowOf = (r, i) => (roomFlows ? roomFlows[i] : r.flow);
  const maxFlow = Math.max(
    ...rooms.map((r, i) => Math.max(toLitersPerHour(flowOf(r, i)), toLitersPerHour(r.designFlow))),
    100
  ) * 1.15;
  const plotH = H - padB - padT;
  const groupW = (W - padL - 8) / rooms.length;

  const yFor = (lph) => padT + plotH * (1 - lph / maxFlow);

  return (
    <svg width={W} height={H}>
      {/* axis */}
      <line x1={padL} y1={padT} x2={padL} y2={H - padB} stroke={COLORS.BORDER} />
      <line x1={padL} y1={H - padB} x2={W - 4} y2={H - padB} stroke={COLORS.BORDER} />
      {[0.25, 0.5, 0.75, 1].map((f) => (
        <g key={f}>
          <line x1={padL} y1={yFor(maxFlow * f)} x2={W - 4} y2={yFor(maxFlow * f)} stroke="rgba(0,0,0,0.06)" />
          <text x={padL - 4} y={yFor(maxFlow * f) + 3} fontSize={9} fill={COLORS.GRAY} textAnchor="end">
            {(maxFlow * f).toFixed(0)}
          </text>
        </g>
      ))}
      <text x={6} y={padT - 2} fontSize={9} fill={COLORS.GRAY}>l/h</text>

      {rooms.map((room, i) => {
        const actual = toLitersPerHour(flowOf(room, i));
        const design = toLitersPerHour(room.designFlow);
        const x0 = padL + groupW * i + groupW * 0.18;
        const barW = groupW * 0.34;
        return (
          <g key={room.id}>
            {/* actual */}
            <rect
              x={x0}
              y={yFor(actual)}
              width={barW}
              height={Math.max(H - padB - yFor(actual), 0)}
              fill={showTargets ? deviationColor(deviations[i]) : COLORS.SECONDARY}
            >
              <title>{room.name}: actual {actual.toFixed(0)} l/h</title>
            </rect>
            {/* design (outline) */}
            {showTargets && (
              <rect
                x={x0 + barW + 2}
                y={yFor(design)}
                width={barW}
                height={Math.max(H - padB - yFor(design), 0)}
                fill="none"
                stroke={COLORS.GRAY}
                strokeDasharray="3 2"
              >
                <title>{room.name}: design {design.toFixed(0)} l/h</title>
              </rect>
            )}
            <text x={padL + groupW * i + groupW / 2} y={H - padB + 12} fontSize={9} fill={COLORS.GRAY} textAnchor="middle">
              R{room.id}
            </text>
          </g>
        );
      })}
      <text x={W - 6} y={H - 4} fontSize={9} fill={COLORS.GRAY} textAnchor="end">
        filled = actual{showTargets ? ' · dashed = design' : ''}
      </text>
    </svg>
  );
};

// ----------------------------------------------------------------------------
// Temperature sparkline
// ----------------------------------------------------------------------------

export const TempSparkline = ({ room, width = 400, height = 90 }) => {
  const pad = 18;
  const history = room.history.length > 1 ? room.history : [room.T, room.T];
  const tMin = Math.min(...history, room.Tset) - 1;
  const tMax = Math.max(...history, room.Tset) + 1;
  const xFor = (i) => pad + ((width - pad - 6) * i) / (history.length - 1);
  const yFor = (t) => 6 + (height - 24) * (1 - (t - tMin) / (tMax - tMin));

  const points = history.map((t, i) => `${xFor(i)},${yFor(t)}`).join(' ');

  return (
    <svg width={width} height={height}>
      {/* setpoint */}
      <line x1={pad} y1={yFor(room.Tset)} x2={width - 6} y2={yFor(room.Tset)} stroke={COLORS.GOOD} strokeDasharray="4 3" />
      <text x={width - 8} y={yFor(room.Tset) - 3} fontSize={9} fill={COLORS.GOOD} textAnchor="end">
        set {room.Tset.toFixed(1)}°C
      </text>
      <polyline points={points} fill="none" stroke={COLORS.HOT} strokeWidth={2} />
      <text x={pad} y={height - 4} fontSize={9} fill={COLORS.GRAY}>
        {room.name} temperature history
      </text>
      <text x={6} y={yFor(history[history.length - 1]) + 3} fontSize={9} fill={COLORS.HOT}>
        {history[history.length - 1].toFixed(1)}
      </text>
    </svg>
  );
};

// ----------------------------------------------------------------------------
// Heating-curve preview
// ----------------------------------------------------------------------------

export const HeatingCurvePreview = ({ slope, offset, tOutdoor, width = 270, height = 120 }) => {
  const padL = 28;
  const padB = 18;
  const tOutMin = OUTDOOR.MIN;
  const tOutMax = OUTDOOR.MAX;
  const tSupMin = 20;
  const tSupMax = HEAT_PUMP.MAX_SUPPLY;

  const xFor = (tOut) => padL + (width - padL - 6) * ((tOut - tOutMin) / (tOutMax - tOutMin));
  const yFor = (tSup) => 6 + (height - padB - 6) * (1 - (tSup - tSupMin) / (tSupMax - tSupMin));

  const points = [];
  for (let t = tOutMin; t <= tOutMax; t += 2) {
    points.push(`${xFor(t)},${yFor(heatingCurveSupply(t, slope, offset))}`);
  }
  const tNow = heatingCurveSupply(tOutdoor, slope, offset);

  return (
    <svg width={width} height={height}>
      <line x1={padL} y1={6} x2={padL} y2={height - padB} stroke={COLORS.BORDER} />
      <line x1={padL} y1={height - padB} x2={width - 4} y2={height - padB} stroke={COLORS.BORDER} />
      {[30, 40, 50, 60].map((t) => (
        <text key={t} x={padL - 3} y={yFor(t) + 3} fontSize={8} fill={COLORS.GRAY} textAnchor="end">{t}°</text>
      ))}
      {[-20, -10, 0, 10, 20].map((t) => (
        <text key={t} x={xFor(t)} y={height - padB + 11} fontSize={8} fill={COLORS.GRAY} textAnchor="middle">{t}°</text>
      ))}
      <polyline points={points.join(' ')} fill="none" stroke={COLORS.PRIMARY} strokeWidth={2} />
      {/* operating point */}
      <circle cx={xFor(tOutdoor)} cy={yFor(tNow)} r={4} fill={COLORS.HOT} />
      <text x={xFor(tOutdoor) + 6} y={yFor(tNow) - 5} fontSize={10} fontWeight={600} fill={COLORS.HOT}>
        {tNow.toFixed(1)}°C
      </text>
      <text x={width - 6} y={12} fontSize={9} fill={COLORS.GRAY} textAnchor="end">supply vs outdoor</text>
    </svg>
  );
};
